import React, { Suspense, lazy } from 'react';

import { ThemeProvider } from '@material-ui/styles';
import PropTypes from 'prop-types';
import { BrowserRouter, Redirect, Switch } from 'react-router-dom';
import { ModalProvider } from 'react-simple-hook-modal';

import AlertMessageModal from '../components/Atoms/AlertMessageModal/AlertMessageModal';
import LoadingScreen from '../components/Atoms/LoadingScreen/LoadingScreen';
import themeConfig from '../utils/themeConfig';
import PrivateRoute from './components/PrivateRoute';
import PublicRoute from './components/PublicRoute';
import Route from './components/Route';

export default function Routes({ auth }: any) {
  //Pages
  const NotFound = lazy(() => import('../scenes/NotFound'));
  const SignInV2 = lazy(() => import('../scenes/NewStyleLogin'));
  const SignUpV2 = lazy(() => import('../scenes/NewStyleSignUp'));
  const NewStyleSuccessfullySignUp = lazy(
    () => import('../scenes/NewStyleSuccessfullySignUp')
  );
  const ActivateAccount = lazy(
    () => import('../scenes/NewStyleActivateAccount')
  );
  const NewStyleActivateAccountSuccess = lazy(
    () => import('../scenes/NewStyleActivateAccountSuccess')
  );
  const NewStyleRecoverPassword = lazy(
    () => import('../scenes/NewStyleRecoverPassword')
  );
  const NewStyleRecoverPasswordSuccess = lazy(
    () => import('../scenes/NewStyleRecoverPasswordSuccess')
  );
  const NewStyleRestorePassword = lazy(
    () => import('../scenes/NewStyleRestorePassword')
  );
  const NewStyleRestorePasswordSuccess = lazy(
    () => import('../scenes/NewStyleRestorePasswordSuccess')
  );

  const Profile = lazy(() => import('../scenes/Profile'));
  const MyBankAccount = lazy(() => import('../scenes/MyBankAccount'));
  const MyVerticals = lazy(() => import('../scenes/MyVerticals'));
  const MyVehicle = lazy(() => import('../scenes/MyVehicle'));

  const Home = lazy(() => import('../scenes/Home'));

  const Certifications = lazy(() => import('../scenes/Certifications'));
  const CertificationProcess = lazy(
    () => import('../scenes/CertificationProcess')
  );
  const CertificationModules = lazy(
    () => import('../scenes/CertificationModule')
  );
  const CertificationQuizzes = lazy(
    () => import('../scenes/CertificationQuiz')
  );

  const Bonus = lazy(() => import('../scenes/Bonus'));
  const BonusSection = lazy(() => import('../scenes/BonusSection'));

  const Payment = lazy(() => import('../scenes/Payment'));
  const PaymentDetail = lazy(() => import('../scenes/PaymentDetail'));

  const Documents = lazy(() => import('../scenes/Documents'));
  const DocumentIdentityVerification = lazy(
    () => import('../scenes/DocumentIdentityVerification')
  );
  const ContractSigning = lazy(() => import('../scenes/ContractSigning'));

  const MyTasks = lazy(() => import('../scenes/MyTasks'));
  const CancelMyTask = lazy(() => import('../scenes/CancelMyTask'));
  const MyTaskDetails = lazy(() => import('../scenes/MyTaskDetails'));

  const AvailableTasks = lazy(() => import('../scenes/AvailableTasks'));
  const AvailableBrands = lazy(() => import('../scenes/AvailableBrands'));
  const AvailableTasksDays = lazy(() => import('../scenes/AvailableTasksDays'));

  const QrValidate = lazy(() => import('../scenes/QrValidate'));

  const Contact = lazy(() => import('../scenes/Contact'));

  const Notifications = lazy(() => import('../scenes/Notifications'));

  const Message = lazy(() => import('../scenes/Message'));

  const DetailMessage = lazy(
    () => import('../scenes/Message/components/DetailMessage')
  );

  const EntryCodeReferred = lazy(() => import('../scenes/EntryCodeReferred'));

  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingScreen />}>
        <ThemeProvider theme={themeConfig}>
          <ModalProvider backdropClassName="modal-provider__main">
            <AlertMessageModal />
            <Switch>
              <Route exact path="/">
                {auth.id === 0 ? (
                  <Redirect to="/sign-in" />
                ) : (
                  <Redirect to="/home" />
                )}
              </Route>

              <PublicRoute exact path="/sign-in" component={SignInV2} />
              <PublicRoute exact path="/sign-up" component={SignUpV2} />
              <Route
                exact
                path="/activate-account/:token"
                component={ActivateAccount}
              />
              <Route
                exact
                path="/activate-account-success"
                component={NewStyleActivateAccountSuccess}
              />
              <Route
                exact
                path="/successfully-sign-up"
                component={NewStyleSuccessfullySignUp}
              />
              <Route
                exact
                path="/recover-password"
                component={NewStyleRecoverPassword}
              />
              <Route
                exact
                path="/recover-password-success"
                component={NewStyleRecoverPasswordSuccess}
              />
              <Route
                exact
                path="/restore-password/:token"
                component={NewStyleRestorePassword}
              />
              <Route
                exact
                path="/restore-password-success"
                component={NewStyleRestorePasswordSuccess}
              />

              <Route exact path="/qr-validate" component={QrValidate} />
              <PrivateRoute exact path="/profile" component={Profile} />
              <PrivateRoute
                exact
                path="/profile/bank-account"
                component={MyBankAccount}
              />

              <PrivateRoute
                exact
                path="/profile/verticals"
                component={MyVerticals}
              />
              <PrivateRoute
                exact
                path="/profile/vehicle"
                component={MyVehicle}
              />
              <PrivateRoute exact path="/home" component={Home} />

              <Route exact path="/certifications" component={Certifications} />
              <Route
                exact
                path="/certifications/:categoryId"
                component={CertificationProcess}
              />
              <Route
                exact
                path="/certification-modules/:certificationId"
                component={CertificationModules}
              />
              <Route
                exact
                path="/certification-quizzes/:certificationModuleId"
                component={CertificationQuizzes}
              />

              <PrivateRoute exact path="/bonus" component={Bonus} />
              <PrivateRoute exact path="/bonus/:id" component={BonusSection} />

              <PrivateRoute exact path="/my-payments" component={Payment} />
              <PrivateRoute
                exact
                path="/my-payments/:dateSelected"
                component={PaymentDetail}
              />

              <PrivateRoute exact path="/documents" component={Documents} />
              <PrivateRoute
                exact
                path="/documents/identity-verification"
                component={DocumentIdentityVerification}
              />
              <PrivateRoute
                exact
                path="/my-contract/contract-signing"
                component={ContractSigning}
              />

              <PrivateRoute exact path="/my-tasks" component={MyTasks} />
              <PrivateRoute
                exact
                path="/cancel-task/:turnId"
                component={CancelMyTask}
              />
              <PrivateRoute
                exact
                path="/my-tasks/:turnId"
                component={MyTaskDetails}
              />

              <PrivateRoute
                exact
                path="/available-tasks"
                component={AvailableTasks}
              />
              <PrivateRoute
                exact
                path="/available-tasks/:roleId/brands/"
                component={AvailableBrands}
              />
              <PrivateRoute
                exact
                path="/available-tasks/:roleId/brands/:brandId"
                component={AvailableTasksDays}
              />

              <PrivateRoute exact path="/contact" component={Contact} />

              <PrivateRoute
                exact
                path="/notifications"
                component={Notifications}
              />

              <PrivateRoute exact path="/message" component={Message} />
              <PrivateRoute
                exact
                path="/detail-message/:id"
                component={DetailMessage}
              />

              <PrivateRoute
                exact
                path="/referred-code"
                component={EntryCodeReferred}
              />

              <Route path="*" component={NotFound} />
            </Switch>
          </ModalProvider>
        </ThemeProvider>
      </Suspense>
    </BrowserRouter>
  );
}

Routes.propTypes = {
  auth: PropTypes.object.isRequired,
};
